import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Mail, Pencil, Phone, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ContactForm } from '../components/ContactForm';
import { DealList } from '../components/DealBits';
import { NewDealForm } from '../components/NewDealForm';
import { OrgPicker } from '../components/Pickers';
import { RemindersPanel } from '../components/RemindersPanel';
import { BuildingChooser } from '../components/BuildingChooser';
import { useCheckOff } from '../components/CheckOff';
import { Timeline } from '../components/Timeline';
import { OUTCOMES, type Outcome } from '../lib/activity';
import { useToast } from '../components/Toast';
import { Button, Card, cx, Empty, ErrorBox, Field, Input, Modal, PageHeader, Pill, Select, Spinner, Textarea } from '../components/ui';
import { CONTACT_TYPES, date, personName, phone, PROPERTY_TYPE_LABEL } from '../lib/format';
import { normalizeEmail, normalizePhone } from '../lib/import/normalize';
import { DEAL_TYPE_LABEL } from '../lib/deals';
import { supabase, unwrap, unwrapMaybe } from '../lib/supabase';

function useContact(id: string) {
  return useQuery({
    queryKey: ['contact', id],
    queryFn: async () => {
      const [contact, history, interactions, counts, deals, owned, dealLinks] = await Promise.all([
        supabase.from('contacts').select('*, organizations(id, name, org_type), contact_emails(*), contact_phones(*)').eq('id', id).single(),
        supabase
          .from('contact_org_history')
          .select('id, changed_at, old:organizations!contact_org_history_old_org_id_fkey(id, name), new:organizations!contact_org_history_new_org_id_fkey(id, name)')
          .eq('contact_id', id)
          .order('changed_at', { ascending: false }),
        supabase
          .from('interactions')
          .select('id, interaction_type, direction, occurred_at, subject, body_html, body_text, snippet, duration_seconds, source, source_link, thread_id, contact_id, participants, has_attachments, body_storage, mail_account_id, external_id, outcome, reminder_id')
          .eq('contact_id', id)
          .order('occurred_at', { ascending: false })
          .limit(1000),
        supabase.from('contact_activity_counts').select('*').eq('contact_id', id).maybeSingle(),
        supabase.from('deals').select('id, name, deal_type, stage, expected_close_date, next_step').eq('primary_contact_id', id).order('created_at', { ascending: false }),
        supabase.from('building_ownership').select('id, buildings(id, name, street, city, state, property_type)').eq('owner_contact_id', id),
        supabase.from('deal_contacts').select('role, deals(id, name, deal_type, stage, expected_close_date, next_step)').eq('contact_id', id),
      ]);
      const extraDeals = unwrap(dealLinks).map((l) => l.deals).filter((d): d is NonNullable<typeof d> => !!d);
      const primaryDeals = unwrap(deals);
      return {
        contact: unwrap(contact),
        history: unwrap(history),
        interactions: unwrap(interactions),
        counts: unwrapMaybe(counts),
        deals: [...primaryDeals, ...extraDeals.filter((d) => !primaryDeals.some((p) => p.id === d.id))],
        primaryDealIds: primaryDeals.map((d) => d.id),
        owned: unwrap(owned),
      };
    },
  });
}

export function ContactDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading, error } = useContact(id);
  const [editing, setEditing] = useState(false);
  const [changingOrg, setChangingOrg] = useState(false);
  const [newDeal, setNewDeal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [changingTypes, setChangingTypes] = useState(false);
  const toast = useToast();
  const refresh = () => qc.invalidateQueries({ queryKey: ['contact', id] });

  const setOrg = useMutation({
    mutationFn: async (orgId: string | null) => unwrap(await supabase.from('contacts').update({ current_org_id: orgId }).eq('id', id)),
    onSuccess: () => {
      setChangingOrg(false);
      refresh();
      qc.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
  const createOrgAndSet = useMutation({
    mutationFn: async (name: string) => {
      const org = unwrap(await supabase.from('organizations').insert({ name }).select('id').single());
      unwrap(await supabase.from('contacts').update({ current_org_id: org.id }).eq('id', id));
    },
    onSuccess: () => {
      setChangingOrg(false);
      refresh();
    },
  });
  const del = useMutation({
    mutationFn: async () => unwrap(await supabase.from('contacts').delete().eq('id', id)),
    onSuccess: () => {
      // Their deals stay, now without a primary contact: remind to assign someone new.
      const orphaned = (data?.deals ?? []).filter((d) => data?.primaryDealIds.includes(d.id));
      for (const d of orphaned) {
        toast({
          message: (
            <>
              Don’t forget to assign a new contact to <b>{d.name}</b>.
            </>
          ),
          action: { label: `Open ${d.name}`, to: `/deals/${d.id}` },
        });
      }
      qc.invalidateQueries({ queryKey: ['contacts'] });
      qc.invalidateQueries({ queryKey: ['pipeline'] });
      qc.invalidateQueries({ queryKey: ['deal'] }); // cached deal pages still show the deleted contact
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      navigate('/contacts');
    },
  });
  const clearReview = useMutation({
    mutationFn: async () => unwrap(await supabase.from('contacts').update({ needs_review: false, review_reason: null }).eq('id', id)),
    onSuccess: refresh,
  });

  if (isLoading) return <Spinner />;
  if (error || !data) return <ErrorBox error={error ?? 'Contact not found'} />;
  const { contact: c, counts } = data;
  const name = personName(c);

  return (
    <div>
      <PageHeader
        title={
          <span>
            {name}
            {c.suffix && <span className="font-normal text-slate-500">, {c.suffix}</span>}
          </span>
        }
        subtitle={
          <div className="flex flex-wrap items-center gap-2">
            {c.salutation && <span>“{c.salutation}”</span>}
            {c.title && <span>{c.title}</span>}
            {c.contact_types.map((t) => (
              <Pill key={t} color="blue" className="capitalize">
                {t}
              </Pill>
            ))}
            <button
              type="button"
              onClick={() => setChangingTypes(true)}
              aria-label={c.contact_types.length ? 'Change type' : 'Add type'}
              className="text-xs font-medium text-indigo-600 hover:underline"
            >
              {c.contact_types.length ? 'Change' : '+ Add type'}
            </button>
            {c.tags.map((t) => (
              <Pill key={t}>{t}</Pill>
            ))}
          </div>
        }
        actions={
          <>
            <Button onClick={() => setNewDeal(true)}>
              <Plus className="h-4 w-4" /> Deal
            </Button>
            <Button onClick={() => setEditing(true)}>
              <Pencil className="h-4 w-4" /> Edit
            </Button>
          </>
        }
      />

      {c.needs_review && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-md border border-orange-200 bg-orange-50 px-3 py-2 text-sm text-orange-900">
          <span>
            <b>Flagged for review:</b> {c.review_reason}
          </span>
          <Button size="sm" onClick={() => clearReview.mutate()}>
            Looks fine
          </Button>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-1">
          <Card title="Company" actions={<Button size="sm" variant="ghost" onClick={() => setChangingOrg(true)}>Change</Button>}>
            {c.organizations ? (
              <Link to={`/companies/${c.organizations.id}`} className="font-medium text-indigo-700 hover:underline">
                {c.organizations.name}
              </Link>
            ) : (
              <span className="text-sm text-slate-400">No company</span>
            )}
            {data.history.length > 1 && (
              <div className="mt-3 border-t border-slate-100 pt-2">
                <div className="mb-1 text-xs font-medium text-slate-500">History</div>
                <ul className="space-y-0.5 text-xs text-slate-600">
                  {data.history.map((h) => (
                    <li key={h.id}>
                      {date(h.changed_at.slice(0, 10))}: {h.old?.name ?? '—'} → {h.new?.name ?? '—'}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Card>

          <ContactInfo contactId={id} emails={c.contact_emails} phones={c.contact_phones} onChange={refresh} />

          {(c.mailing_street || c.mailing_city || c.notes) && (
            <Card title="Details">
              {(c.mailing_street || c.mailing_city) && (
                <div className="mb-2 text-sm text-slate-700">
                  {c.mailing_street}
                  <br />
                  {[c.mailing_city, c.mailing_state, c.mailing_zip].filter(Boolean).join(', ')}
                </div>
              )}
              {c.notes && <p className="whitespace-pre-wrap text-sm text-slate-600">{c.notes}</p>}
            </Card>
          )}

          <Card title="Reminders">
            <RemindersPanel contactId={id} label={name} />
          </Card>

          <OwnsCard contactId={id} owned={data.owned} onChange={refresh} />
          <div className="text-xs text-slate-400">
            Source: {c.source.replace('_', ' ')} ·{' '}
            <button className="hover:text-rose-600 hover:underline" onClick={() => setDeleting(true)}>
              <Trash2 className="inline h-3 w-3" /> delete contact
            </button>
          </div>
          <ErrorBox error={del.error} />
        </div>

        <div className="space-y-4 lg:col-span-2">
          {counts && <ActivitySummary counts={counts} />}
          <Card title="Deals">
            <DealList deals={data.deals} />
          </Card>
          <Card title="Log activity">
            <QuickLog contactId={id} orgId={c.current_org_id} name={name} onLogged={refresh} />
          </Card>
          <Card title="Timeline">
            <Timeline items={data.interactions} />
          </Card>
        </div>
      </div>

      <Modal title={`Edit ${name}`} open={editing} onClose={() => setEditing(false)} wide>
        <ContactForm
          contact={c}
          onDone={() => {
            setEditing(false);
            refresh();
          }}
        />
      </Modal>
      <Modal title="Change company" open={changingOrg} onClose={() => setChangingOrg(false)}>
        <ChangeOrg
          onPick={(orgId) => setOrg.mutate(orgId)}
          onCreate={(n) => createOrgAndSet.mutate(n)}
          onRemove={c.current_org_id ? () => setOrg.mutate(null) : undefined}
        />
        <ErrorBox error={setOrg.error ?? createOrgAndSet.error} />
      </Modal>
      <Modal title="Type" open={changingTypes} onClose={() => setChangingTypes(false)}>
        <TypesEditor
          contactId={id}
          types={c.contact_types}
          onDone={() => {
            setChangingTypes(false);
            refresh();
          }}
        />
      </Modal>
      <Modal title={`Delete ${name}?`} open={deleting} onClose={() => setDeleting(false)}>
        <DeleteContact
          name={name}
          primaryDeals={data.deals.filter((d) => data.primaryDealIds.includes(d.id))}
          teamDeals={data.deals.filter((d) => !data.primaryDealIds.includes(d.id))}
          busy={del.isPending}
          onCancel={() => setDeleting(false)}
          onConfirm={() => del.mutate()}
        />
        <ErrorBox error={del.error} />
      </Modal>
      <Modal title="New deal" open={newDeal} onClose={() => setNewDeal(false)}>
        <NewDealForm contact={{ id, name }} orgId={c.current_org_id} defaultName={c.organizations?.name ?? name} />
      </Modal>
    </div>
  );
}

function DeleteContact({
  name,
  primaryDeals,
  teamDeals,
  busy,
  onCancel,
  onConfirm,
}: {
  name: string;
  primaryDeals: { id: string; name: string; deal_type: string }[];
  teamDeals: { id: string; name: string; deal_type: string }[];
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const list = (deals: typeof primaryDeals) => (
    <ul className="my-2 space-y-1">
      {deals.map((d) => (
        <li key={d.id} className="flex items-center gap-2">
          <span className="font-medium text-slate-900">{d.name}</span>
          <Pill>{DEAL_TYPE_LABEL[d.deal_type] ?? d.deal_type}</Pill>
        </li>
      ))}
    </ul>
  );
  return (
    <div className="space-y-3 text-sm text-slate-700">
      {primaryDeals.length > 0 && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
          <p>
            <b>{name}</b> is the primary contact on {primaryDeals.length === 1 ? 'this deal' : `these ${primaryDeals.length} deals`}:
          </p>
          {list(primaryDeals)}
          <p>{primaryDeals.length === 1 ? 'The deal stays' : 'The deals stay'}, with no contact until you assign a new one.</p>
        </div>
      )}
      {teamDeals.length > 0 && (
        <div>
          <p>They’ll also be removed from:</p>
          {list(teamDeals)}
        </div>
      )}
      <p>Their emails, phone numbers and activity history are deleted. This can’t be undone.</p>
      <div className="flex justify-end gap-2">
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="danger" busy={busy} onClick={onConfirm}>
          Delete {name}
        </Button>
      </div>
    </div>
  );
}

function ChangeOrg({ onPick, onCreate, onRemove }: { onPick: (id: string) => void; onCreate: (name: string) => void; onRemove?: () => void }) {
  const [name, setName] = useState('');
  return (
    <div className="space-y-4">
      <OrgPicker onPick={(o) => onPick(o.id)} />
      <div className="border-t border-slate-100 pt-3">
        <div className="mb-1 text-xs font-medium text-slate-600">Or create a new company</div>
        <div className="flex gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Company name" />
          <Button onClick={() => name.trim() && onCreate(name.trim())} disabled={!name.trim()}>
            Create
          </Button>
        </div>
      </div>
      {onRemove && (
        <button className="text-xs text-slate-500 hover:text-rose-600 hover:underline" onClick={onRemove}>
          Remove company (no current company)
        </button>
      )}
    </div>
  );
}

function ActivitySummary({ counts }: { counts: Record<string, number | string | null> }) {
  const line = (suffix: string) => {
    const parts = (['call', 'email', 'text', 'meeting'] as const)
      .map((t) => [t, Number(counts[`${t}s_${suffix}`] ?? 0)] as const)
      .filter(([, n]) => n > 0)
      .map(([t, n]) => `${n} ${t}${n === 1 ? '' : 's'}`);
    return parts.length ? parts.join(', ') : 'nothing';
  };
  return (
    <div className="grid grid-cols-3 gap-2 text-sm">
      {[
        ['This week', '7d'],
        ['30 days', '30d'],
        ['90 days', '90d'],
      ].map(([label, s]) => (
        <div key={s} className="rounded-lg border border-slate-200 bg-white px-3 py-2">
          <div className="text-xs text-slate-500">{label}</div>
          <div className="text-slate-800">{line(s!)}</div>
        </div>
      ))}
    </div>
  );
}

function QuickLog({ contactId, orgId, name, onLogged }: { contactId: string; orgId: string | null; name: string; onLogged: () => void }) {
  const [type, setType] = useState<'call' | 'note' | 'meeting' | 'text' | 'email'>('call');
  const [direction, setDirection] = useState<'out' | 'in'>('out');
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [when, setWhen] = useState('');
  const { afterLogged } = useCheckOff();
  const log = useMutation({
    mutationFn: async () => {
      const row = unwrap(
        await supabase
          .from('interactions')
          .insert({
            contact_id: contactId,
            org_id: orgId,
            interaction_type: type,
            direction: type === 'note' ? null : direction,
            outcome: type === 'call' ? outcome : null,
            occurred_at: when ? new Date(when).toISOString() : new Date().toISOString(),
            subject: subject.trim() || null,
            body_text: body.trim() || null,
            snippet: body.trim().slice(0, 200) || null,
            source: 'manual',
          })
          .select('id')
          .single(),
      );
      // One action, one activity: offer to complete an open reminder of the same kind instead.
      const open =
        type === 'call' || type === 'meeting'
          ? unwrap(await supabase.from('reminders').select('id, note').eq('contact_id', contactId).eq('activity_type', type).neq('status', 'done').order('due_date'))
          : [];
      return { id: row.id, open };
    },
    onSuccess: ({ id, open }) => {
      setSubject('');
      setBody('');
      setWhen('');
      setOutcome(null);
      onLogged();
      if (type === 'call' || type === 'meeting') afterLogged({ interactionId: id, type, who: { contactId, dealId: null, name }, openReminders: open });
    },
  });
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        <Select value={type} onChange={(e) => setType(e.target.value as typeof type)} className="!w-32">
          <option value="call">Call</option>
          <option value="note">Note</option>
          <option value="meeting">Meeting</option>
          <option value="text">Text</option>
          <option value="email">Email</option>
        </Select>
        {type !== 'note' && (
          <Select value={direction} onChange={(e) => setDirection(e.target.value as 'in' | 'out')} className="!w-32">
            <option value="out">I reached out</option>
            <option value="in">They reached out</option>
          </Select>
        )}
        <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className="!w-52" title="Leave blank for now" />
      </div>
      {type === 'call' && (
        <div className="flex flex-wrap gap-1.5" aria-label="Call outcome">
          {OUTCOMES.map((o) => (
            <button
              key={o.key}
              onClick={() => setOutcome(outcome === o.key ? null : o.key)}
              className={cx('rounded-full border px-2.5 py-0.5 text-xs', outcome === o.key ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-slate-200 text-slate-600 hover:border-indigo-300')}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
      <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject (optional)" />
      <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Notes" />
      <ErrorBox error={log.error} />
      <div className="flex justify-end">
        <Button variant="primary" busy={log.isPending} disabled={!body.trim() && !subject.trim() && !(type === 'call' && outcome)} onClick={() => log.mutate()}>
          Log {type}
        </Button>
      </div>
    </div>
  );
}

function ContactInfo({
  contactId,
  emails,
  phones,
  onChange,
}: {
  contactId: string;
  emails: { id: string; email: string; is_primary: boolean }[];
  phones: { id: string; phone_e164: string; extension: string | null; phone_type: string; is_primary: boolean }[];
  onChange: () => void;
}) {
  const [adding, setAdding] = useState<'email' | 'phone' | null>(null);
  const [value, setValue] = useState('');
  const [phoneType, setPhoneType] = useState('mobile');
  const add = useMutation({
    mutationFn: async () => {
      if (adding === 'email') {
        const e = normalizeEmail(value);
        if (!e.email) throw new Error(e.problem ?? 'Enter an email');
        unwrap(await supabase.from('contact_emails').insert({ contact_id: contactId, email: e.email, is_primary: emails.length === 0 }));
      } else {
        const p = normalizePhone(value);
        if (!p.phone) throw new Error(p.problem ?? 'Enter a phone number');
        unwrap(
          await supabase.from('contact_phones').insert({ contact_id: contactId, phone_e164: p.phone.e164, extension: p.phone.extension, phone_type: phoneType, is_primary: phones.length === 0 }),
        );
      }
    },
    onSuccess: () => {
      setAdding(null);
      setValue('');
      onChange();
    },
  });
  const remove = useMutation({
    mutationFn: async ({ table, id }: { table: 'contact_emails' | 'contact_phones'; id: string }) => unwrap(await supabase.from(table).delete().eq('id', id)),
    onSuccess: onChange,
  });

  return (
    <Card
      title="Contact info"
      actions={
        <>
          <Button size="sm" variant="ghost" onClick={() => setAdding('email')}>
            + Email
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setAdding('phone')}>
            + Phone
          </Button>
        </>
      }
    >
      <ul className="space-y-1.5 text-sm">
        {emails.map((e) => (
          <li key={e.id} className="group flex items-center gap-2">
            <Mail className="h-3.5 w-3.5 text-slate-400" />
            <a href={`mailto:${e.email}`} className="truncate text-indigo-700 hover:underline">
              {e.email}
            </a>
            <button className="ml-auto hidden text-slate-300 hover:text-rose-600 group-hover:block" onClick={() => remove.mutate({ table: 'contact_emails', id: e.id })} aria-label="Remove">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </li>
        ))}
        {phones.map((p) => (
          <li key={p.id} className="group flex items-center gap-2">
            <Phone className="h-3.5 w-3.5 text-slate-400" />
            <a href={`tel:${p.phone_e164}`} className="text-slate-800 hover:underline">
              {phone(p.phone_e164, p.extension)}
            </a>
            <span className="text-xs capitalize text-slate-400">{p.phone_type}</span>
            <button className="ml-auto hidden text-slate-300 hover:text-rose-600 group-hover:block" onClick={() => remove.mutate({ table: 'contact_phones', id: p.id })} aria-label="Remove">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </li>
        ))}
        {!emails.length && !phones.length && <li className="text-slate-400">No email or phone.</li>}
      </ul>
      {adding && (
        <div className="mt-3 space-y-2 border-t border-slate-100 pt-3">
          <Field label={adding === 'email' ? 'New email' : 'New phone'}>
            <div className="flex gap-2">
              <Input value={value} onChange={(e) => setValue(e.target.value)} autoFocus />
              {adding === 'phone' && (
                <Select value={phoneType} onChange={(e) => setPhoneType(e.target.value)} className="!w-28">
                  <option value="mobile">Mobile</option>
                  <option value="office">Office</option>
                  <option value="other">Other</option>
                </Select>
              )}
              <Button variant="primary" busy={add.isPending} onClick={() => add.mutate()}>
                Add
              </Button>
            </div>
          </Field>
          <ErrorBox error={add.error} />
        </div>
      )}
    </Card>
  );
}

/** Contact types ("designations"): add, remove or change — any combination. */
function TypesEditor({ contactId, types, onDone }: { contactId: string; types: string[]; onDone: () => void }) {
  const qc = useQueryClient();
  const [picked, setPicked] = useState<string[]>(types);
  const save = useMutation({
    mutationFn: async () => unwrap(await supabase.from('contacts').update({ contact_types: picked }).eq('id', contactId)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts'] });
      onDone();
    },
  });
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">Pick as many as apply — e.g. investor and broker.</p>
      <div className="flex flex-wrap gap-2">
        {CONTACT_TYPES.map((t) => {
          const on = picked.includes(t);
          return (
            <button key={t} type="button" aria-pressed={on} onClick={() => setPicked(on ? picked.filter((x) => x !== t) : [...picked, t])}>
              <Pill color={on ? 'blue' : 'gray'} className={on ? 'px-3 py-1 text-sm capitalize' : 'px-3 py-1 text-sm capitalize opacity-70 hover:opacity-100'}>
                {on ? '✓ ' : '+ '}
                {t}
              </Pill>
            </button>
          );
        })}
      </div>
      <ErrorBox error={save.error} />
      <div className="flex justify-end gap-2">
        <Button onClick={onDone}>Cancel</Button>
        <Button variant="primary" busy={save.isPending} onClick={() => save.mutate()}>
          Save
        </Button>
      </div>
    </div>
  );
}

type Owned = { id: string; buildings: { id: string; name: string | null; street: string | null; city: string | null; property_type: string | null } | null };

/** Buildings this contact owns: add an existing one, create a new one in place, or remove the link. */
function OwnsCard({ contactId, owned, onChange }: { contactId: string; owned: Owned[]; onChange: () => void }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const add = useMutation({
    mutationFn: async (buildingId: string) => {
      if (owned.some((o) => o.buildings?.id === buildingId)) return; // already linked
      unwrap(await supabase.from('building_ownership').insert({ building_id: buildingId, owner_contact_id: contactId, source: 'manual' }));
    },
    onSuccess: () => {
      setAdding(false);
      onChange();
      qc.invalidateQueries({ queryKey: ['buildings'] });
    },
  });
  // Removes the ownership link only; the building itself stays in the CRM.
  const remove = useMutation({
    mutationFn: async (ownershipId: string) => unwrap(await supabase.from('building_ownership').delete().eq('id', ownershipId)),
    onSuccess: () => {
      onChange();
      qc.invalidateQueries({ queryKey: ['buildings'] });
    },
  });
  return (
    <Card title="Owns" actions={<Button size="sm" variant="ghost" onClick={() => setAdding(true)}>+ Add</Button>}>
      {owned.length === 0 ? (
        <Empty>No buildings.</Empty>
      ) : (
        <ul className="space-y-1.5 text-sm">
          {owned.map((o) =>
            o.buildings ? (
              <li key={o.id} className="group flex items-center gap-2">
                <Link to={`/buildings/${o.buildings.id}`} className="flex min-w-0 flex-1 items-center gap-2 hover:text-indigo-700">
                  <Building2 className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                  <span className="truncate">
                    {o.buildings.name || o.buildings.street || 'Unknown address'}
                    {o.buildings.city && <span className="text-slate-500">, {o.buildings.city}</span>}
                  </span>
                  {o.buildings.property_type && <Pill className="ml-auto">{PROPERTY_TYPE_LABEL[o.buildings.property_type]}</Pill>}
                </Link>
                <button
                  className="text-slate-300 hover:text-rose-600"
                  onClick={() => confirm(`Remove ${o.buildings!.name || o.buildings!.street || 'this building'} from this contact? The building stays in the CRM.`) && remove.mutate(o.id)}
                  aria-label="Remove building"
                  title="Remove from this contact"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </li>
            ) : null,
          )}
        </ul>
      )}
      <ErrorBox error={add.error ?? remove.error} />
      <Modal title="Add a building they own" open={adding} onClose={() => setAdding(false)}>
        <BuildingChooser onChosen={(b) => add.mutate(b.id)} />
        <p className="mt-2 text-xs text-slate-500">Not found? Choose “Create new building” — you’ll stay on this page.</p>
      </Modal>
    </Card>
  );
}
